import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react'
import { useNavigate } from 'react-router-dom'
import Typography from '@mui/material/Typography'
import Tooltip from '@mui/material/Tooltip'
import CircularProgress from '@mui/material/CircularProgress'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faArchive,
  faArrowRotateLeft,
  faBoxArchive,
  faChevronRight,
  faCircleInfo,
  faClone,
  faClockRotateLeft,
  faFileLines,
  faListUl,
  faPen,
  faPlus,
  faSparkles,
  faToggleOff,
  faToggleOn,
  faTrashCan,
  faXmark,
} from '@fortawesome/pro-regular-svg-icons'
import { faBoxArchive as faBoxArchiveSolid } from '@fortawesome/pro-solid-svg-icons'
import {
  ChatDocumentDocType,
  presignChatDocumentAPI,
  uploadFileToPresignedUrl,
} from '@labrav/common'
import {
  FundingTemplateActivityEntry,
  FundingTemplateCreateBody,
  FundingTemplateDetail,
  FundingTemplateFieldOverride,
  FundingTemplateFileTypeOption,
  FundingTemplateFormField,
  FundingTemplateFormSchemaResponse,
  FundingTemplateResourceDocument,
  FundingTemplateRuleSuggestion,
  archiveFundingTemplate,
  cloneFundingTemplate,
  createFundingTemplate,
  enableFundingTemplate,
  FundingTemplateUsageEntry,
  getFundingTemplateActivity,
  getFundingTemplateFormSchema,
  getFundingTemplateRuleSuggestions,
  listFundingTemplateUsages,
  patchFundingTemplate,
  suggestFundingTemplateName,
} from '../../api/fundingApi'
import { formatWalletDate } from '../../utils/format'
import { AutoSubmitLimitNote } from './AutoSubmitLimitNote'
import {
  DynamicSchemaFields,
  isPricingCalculatorSchemaPath,
  schemaFieldDisplayLabel,
  schemaFieldTooltip,
} from './DynamicSchemaFields'
import { displayFundingProgramLabel } from './fundingProgramRows'
import {
  SearchableCheckboxOption,
  SearchableCheckboxSelect,
} from './SearchableCheckboxSelect'
import { FundingConfirmDialog } from './FundingConfirmDialog'
import { TemplateActivityLogModal } from './TemplateActivityLogModal'
import { TemplateDetailBreadcrumb } from './TemplateDetailBreadcrumb'
import { TemplateUsagesModal } from './TemplateUsagesModal'
import {
  FieldOverrideState,
  validatePricingCalculatorUrl,
  validateTemplateFields,
} from './validateTemplateFields'
import { useStyles } from './TemplateDetailPage.styles'

type PersistedTemplateSnapshot = {
  name: string
  description: string
  status: 'active' | 'disabled' | 'archived'
  overrides: FieldOverrideState
  forceAws: boolean
  calculatorUrl: string
  documents: FundingTemplateResourceDocument[]
  addedPaths: string[]
}

const ARCHIVE_CONFIRM = {
  title: 'Archive this template?',
  body:
    'Are you sure you want to archive this template? Once archived, it will not be available for partners to select in new funding requests. Existing requests will not be affected.',
  confirmLabel: 'Archive',
} as const

const UNARCHIVE_CONFIRM = {
  title: 'Unarchive this template?',
  body:
    'Are you sure you want to unarchive this template? Once unarchived, it will be available for partners to select in new funding requests.',
  confirmLabel: 'Unarchive',
} as const

const ARCHIVED_EDIT_TOOLTIP = 'Please unarchive to edit'

/** Figma Desktop-1811 — switch Active → Inactive. */
const STATUS_INACTIVE_CONFIRM = {
  title: 'Switch template status to inactive?',
  body:
    'Are you sure you want to switch this template status to inactive? You will not be able to use this template for any funding programs.\n\nYou can undo this action anytime.',
  confirmLabel: 'Continue',
} as const

const STATUS_ACTIVE_CONFIRM = {
  title: 'Switch template status to active?',
  body:
    'Are you sure you want to switch this template status to active? You will be able to use this template for funding programs.\n\nYou can undo this action anytime.',
  confirmLabel: 'Continue',
} as const

type SlotPendingUpload = {
  id: string
  filename: string
}

type DocumentUploadSlot = {
  id: string
  businessUseCase: string
  documents: FundingTemplateResourceDocument[]
  pending: SlotPendingUpload[]
}

const newUploadSlotId = () =>
  `slot-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`

const newPendingUploadId = () =>
  `pending-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`

const emptyUploadSlot = (): DocumentUploadSlot => ({
  id: newUploadSlotId(),
  businessUseCase: '',
  documents: [],
  pending: [],
})

const slotsFromDocuments = (
  docs: FundingTemplateResourceDocument[]
): DocumentUploadSlot[] => {
  if (docs.length === 0) return [emptyUploadSlot()]
  return docs.map(doc => ({
    id: newUploadSlotId(),
    businessUseCase: doc.business_use_case ?? '',
    documents: [doc],
    pending: [],
  }))
}

const flattenSlotDocuments = (slots: DocumentUploadSlot[]) =>
  slots.flatMap(slot => slot.documents)

const DEFAULT_NEW_TEMPLATE_NAME = 'Untitled template'
const MAX_TEMPLATE_NAME_LENGTH = 300

const DEFAULT_FILE_HELP =
  'Select a document type, then upload a PDF, DOC, or DOCX. Attachments are optional unless required by the funding type.'

const SOW_BUSINESS_USE_CASE = 'Project Plan/Checklist'

const CHAT_DOC_TYPES: ReadonlySet<string> = new Set([
  'pricing_calculator',
  'sow',
  'project_plan',
  'poc_credit_checklist',
  'business_case',
  'customer_signoff',
  'transcript',
  'apid_proof',
  'other',
])

/** Map schema/API doc_type strings onto the chat-presign union. */
const toChatDocType = (raw: string): ChatDocumentDocType =>
  CHAT_DOC_TYPES.has(raw) ? (raw as ChatDocumentDocType) : 'other'

const documentsFromResources = (
  resources: FundingTemplateDetail['resources'] | null | undefined
): FundingTemplateResourceDocument[] => {
  const fromList = (resources?.documents ?? []).filter(
    doc => Boolean(doc?.s3_uri?.trim())
  )
  if (fromList.length > 0) {
    return fromList.map(doc => ({
      ...doc,
      business_use_case:
        doc.business_use_case ||
        (doc.doc_type === 'sow' || doc.doc_type === 'project_plan'
          ? SOW_BUSINESS_USE_CASE
          : undefined),
    }))
  }
  const sow = resources?.sow
  if (sow?.s3_uri?.trim()) {
    return [
      {
        s3_uri: sow.s3_uri,
        doc_type: sow.doc_type || 'sow',
        filename: sow.filename,
        business_use_case: sow.business_use_case || SOW_BUSINESS_USE_CASE,
      },
    ]
  }
  return []
}

const snapshotFromTemplate = (
  detail: FundingTemplateDetail,
  addedPaths: string[] = []
): PersistedTemplateSnapshot => ({
  name: detail.name ?? '',
  description: detail.description ?? '',
  status: detail.status,
  overrides: { ...(detail.field_overrides ?? {}) },
  forceAws: detail.force_aws_account_id,
  calculatorUrl: detail.resources?.pricing_calculator_url ?? '',
  documents: documentsFromResources(detail.resources),
  addedPaths: [...addedPaths],
})

const snapshotsEqual = (
  a: PersistedTemplateSnapshot,
  b: PersistedTemplateSnapshot
) => JSON.stringify(a) === JSON.stringify(b)

export type TemplateDetailMode = 'create' | 'edit'

export interface TemplateDetailPageProps {
  mode: TemplateDetailMode
  serviceUrl: string
  partnerId: string
  programLabel: string
  benefitIdentifier: string
  template?: FundingTemplateDetail | null
  /** Catalog / automation program blurb shown as read-only Program description. */
  programDescription?: string | null
  /** Auto-submit eligibility copy for the program. */
  eligibilityCriteria?: string | null
  autoSubmitLimitUsd?: string | null
  globalDefaults?: Record<string, string | null> | null
  globalsConfigured?: boolean
  updaterName?: string
  updaterLabraUserId?: string
  /** When set, breadcrumbs replace the settings page header title. */
  onHeaderChange?: (header: ReactNode | null) => void
  onDiscard: () => void
  onSaved: (
    detail: FundingTemplateDetail,
    meta?: { kind?: 'saved' | 'autosaved' | 'archived' | 'unarchived' }
  ) => void
  onCloned: (detail: FundingTemplateDetail) => void
  onError: (message: string) => void
}

const createdByLine = (
  createdBy: string | null | undefined,
  createdAt: string | null | undefined
) => {
  const date = formatWalletDate(createdAt)
  if (createdBy?.trim() && date) return `${createdBy.trim()} on ${date}`
  if (createdBy?.trim()) return createdBy.trim()
  return date ?? '—'
}

export const TemplateDetailPage = ({
  mode,
  serviceUrl,
  partnerId,
  programLabel,
  benefitIdentifier,
  template,
  programDescription = null,
  eligibilityCriteria = null,
  autoSubmitLimitUsd = null,
  globalDefaults,
  globalsConfigured = false,
  updaterName,
  updaterLabraUserId,
  onHeaderChange,
  onDiscard,
  onSaved,
  onCloned,
  onError,
}: TemplateDetailPageProps) => {
  const { classes, cx } = useStyles()
  const navigate = useNavigate()
  const pageShellRef = useRef<HTMLDivElement | null>(null)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [status, setStatus] = useState<'active' | 'disabled' | 'archived'>(
    'active'
  )
  const readOnlyArchived = status === 'archived'
  const [suggestions, setSuggestions] = useState<
    FundingTemplateRuleSuggestion[]
  >([])
  const suggestionsRef = useRef(suggestions)
  suggestionsRef.current = suggestions

  // Open at the top — preserve scroll from the settings list otherwise.
  useEffect(() => {
    try {
      window.scrollTo(0, 0)
    } catch {
      /* jsdom */
    }
    document.documentElement.scrollTop = 0
    document.body.scrollTop = 0
    let node: HTMLElement | null = pageShellRef.current
    while (node) {
      if (node.scrollTop) node.scrollTop = 0
      node = node.parentElement
    }
  }, [mode, template?.template_id, benefitIdentifier])
  /** Quiet chip fetch on open — does not lock the form. */
  const [suggestionsLoading, setSuggestionsLoading] = useState(false)
  /** Explicit Rewrite click — locks fields and shows Rewriting…. */
  const [isRegenerating, setIsRegenerating] = useState(false)
  const [schema, setSchema] =
    useState<FundingTemplateFormSchemaResponse | null>(null)
  /** Static-across-apps fields from form-schema (default visible). */
  const [staticFields, setStaticFields] = useState<FundingTemplateFormField[]>(
    []
  )
  /** Full schema flatten for Add-field picker. */
  const [availableFields, setAvailableFields] = useState<
    FundingTemplateFormField[]
  >([])
  /** User-added paths (not in static defaults). */
  const [addedPaths, setAddedPaths] = useState<string[]>([])
  const [addFieldOpen, setAddFieldOpen] = useState(false)
  const [overrides, setOverrides] = useState<FieldOverrideState>({})
  /** Snapshot for create seed only — avoid reseeding when parent defaults identity changes. */
  const globalDefaultsRef = useRef(globalDefaults)
  globalDefaultsRef.current = globalDefaults
  const [forceAws, setForceAws] = useState(false)
  const [calculatorUrl, setCalculatorUrl] = useState('')
  const [documents, setDocuments] = useState<FundingTemplateResourceDocument[]>(
    []
  )
  const [uploadSlots, setUploadSlots] = useState<DocumentUploadSlot[]>([
    emptyUploadSlot(),
  ])
  const [saving, setSaving] = useState(false)
  const [cloning, setCloning] = useState(false)
  /** Form-schema only — Basic information / resources. Other sections load independently. */
  const [schemaLoading, setSchemaLoading] = useState(true)
  /** Full template GET still in flight after list-stub navigation. */
  const templateDetailPending =
    mode === 'edit' && Boolean(template) && template?.field_overrides === null
  const [activityOpen, setActivityOpen] = useState(false)
  const [activityEntries, setActivityEntries] = useState<
    FundingTemplateActivityEntry[]
  >([])
  const [activityLoading, setActivityLoading] = useState(false)
  const [usagesOpen, setUsagesOpen] = useState(false)
  const [usagesEntries, setUsagesEntries] = useState<
    FundingTemplateUsageEntry[]
  >([])
  const [usagesLoading, setUsagesLoading] = useState(false)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const [archiveConfirm, setArchiveConfirm] = useState<
    'archive' | 'unarchive' | null
  >(null)
  const [statusConfirm, setStatusConfirm] = useState<
    'activate' | 'deactivate' | null
  >(null)
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const nameInputRef = useRef<HTMLInputElement | null>(null)
  const activeUploadSlotIdRef = useRef<string | null>(null)
  /** Page-open / last-persisted snapshot for Reset. */
  const pageOpenSnapshotRef = useRef<PersistedTemplateSnapshot | null>(null)
  const lastAutosavedRef = useRef<PersistedTemplateSnapshot | null>(null)
  const autosaveGenRef = useRef(0)
  const [calculatorFocused, setCalculatorFocused] = useState(false)
  const [autosaveStatus, setAutosaveStatus] = useState<
    'idle' | 'saving' | 'saved' | 'error'
  >('idle')
  const [createUnsavedOpen, setCreateUnsavedOpen] = useState(false)
  const fieldsLocked = readOnlyArchived || isRegenerating

  const isCreateDirty = useMemo(() => {
    if (mode !== 'create') return false
    if (name.trim() && name.trim() !== DEFAULT_NEW_TEMPLATE_NAME) return true
    if (description.trim()) return true
    if (calculatorUrl.trim()) return true
    if (documents.length > 0) return true
    if (addedPaths.length > 0) return true
    if (status !== 'active') return true
    if (forceAws) return true
    return Object.values(overrides).some(o => String(o?.value ?? '').trim())
  }, [
    mode,
    name,
    description,
    calculatorUrl,
    documents,
    addedPaths,
    status,
    forceAws,
    overrides,
  ])

  const requestDiscard = useCallback(() => {
    if (mode === 'create' && isCreateDirty) {
      setCreateUnsavedOpen(true)
      return
    }
    onDiscard()
  }, [mode, isCreateDirty, onDiscard])

  const benefitId =
    mode === 'edit' && template
      ? template.sub_funding_type.benefit_identifier
      : benefitIdentifier

  const subFundingLabel = displayFundingProgramLabel(
    mode === 'edit' && template
      ? template.sub_funding_type.program_name
      : programLabel
  )

  const breadcrumbName =
    name.trim() ||
    (mode === 'create'
      ? DEFAULT_NEW_TEMPLATE_NAME
      : template?.name?.trim() || DEFAULT_NEW_TEMPLATE_NAME)

  // Push breadcrumbs into the settings page header (replaces "Funding request").
  useEffect(() => {
    if (!onHeaderChange) return
    onHeaderChange(
      <TemplateDetailBreadcrumb
        current={breadcrumbName}
        onBackToSettings={requestDiscard}
      />
    )
    return () => onHeaderChange(null)
  }, [onHeaderChange, breadcrumbName, requestDiscard])

  const fileTypes: FundingTemplateFileTypeOption[] = useMemo(
    () => schema?.resources.file_types ?? [],
    [schema]
  )
  const showTypedAttach = fileTypes.length > 0 || documents.length > 0
  const showPricing =
    Boolean(schema?.resources.supports_pricing_calculator) ||
    Boolean(calculatorUrl.trim())
  const showResourcesCard = !schemaLoading

  // Hydrate from template when id changes, or when list-stub upgrades to full detail
  // (field_overrides null → object). Skip plain autosave parent refreshes.
  const templateIsStub = template?.field_overrides === null
  useEffect(() => {
    if (mode === 'edit' && template) {
      const snap = snapshotFromTemplate(template)
      setName(snap.name.slice(0, MAX_TEMPLATE_NAME_LENGTH))
      setDescription(snap.description)
      setOverrides(snap.overrides)
      setForceAws(snap.forceAws)
      setStatus(snap.status)
      setCalculatorUrl(snap.calculatorUrl)
      setDocuments(snap.documents)
      setUploadSlots(slotsFromDocuments(snap.documents))
      // Stub has no overrides yet — keep page-open snapshot until full detail arrives.
      if (!templateIsStub) {
        pageOpenSnapshotRef.current = snap
        lastAutosavedRef.current = snap
      }
      setAutosaveStatus('idle')
    } else {
      setName(DEFAULT_NEW_TEMPLATE_NAME)
      setDescription('')
      setOverrides({})
      setForceAws(false)
      setStatus('active')
      setCalculatorUrl('')
      setDocuments([])
      setUploadSlots([emptyUploadSlot()])
      pageOpenSnapshotRef.current = null
      lastAutosavedRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- id + stub→full only
  }, [mode, template?.template_id, templateIsStub])

  useEffect(() => {
    if (!fileTypes.length) return
    setUploadSlots(prev => {
      if (prev.length === 0) return [emptyUploadSlot()]
      // Keep blank slots blank; only remap types that no longer exist in schema.
      return prev.map(slot => {
        if (!slot.businessUseCase) return slot
        return fileTypes.some(t => t.business_use_case === slot.businessUseCase)
          ? slot
          : { ...slot, businessUseCase: '' }
      })
    })
  }, [fileTypes])

  const fileTypeOptions: SearchableCheckboxOption[] = useMemo(
    () =>
      fileTypes.map(option => ({
        value: option.business_use_case,
        label: option.label,
      })),
    [fileTypes]
  )

  const loadSuggestions = useCallback(
    async (opts?: { regenerate?: boolean }) => {
      if (!benefitId) {
        setSuggestions([])
        return
      }
      const regenerate = Boolean(opts?.regenerate)
      setSuggestionsLoading(true)
      if (regenerate) setIsRegenerating(true)
      try {
        const { data } = await getFundingTemplateRuleSuggestions(
          serviceUrl,
          partnerId,
          {
            benefitIdentifier: benefitId,
            // On regenerate, send current AI input so the model can vary chips.
            description,
            regenerate,
          }
        )
        const next = data.suggestions ?? []
        let applied = next
        if (regenerate && next.length > 0) {
          const prev = suggestionsRef.current
          const same =
            prev.length === next.length &&
            prev.every(
              (item, i) =>
                item.label === next[i]?.label && item.text === next[i]?.text
            )
          // Identical set → rotate so regenerate is not a silent no-op.
          applied = same && next.length > 1 ? [...next.slice(1), next[0]] : next
          // Fill AI input with the leading chip. Typed draft was already sent
          // as description so the model can take it into consideration.
          if (applied[0]?.text) setDescription(applied[0].text)
        }
        setSuggestions(applied)
      } catch {
        if (!regenerate) setSuggestions([])
      } finally {
        setSuggestionsLoading(false)
        if (regenerate) setIsRegenerating(false)
      }
    },
    [benefitId, serviceUrl, partnerId, description]
  )

  useEffect(() => {
    void loadSuggestions()
    // Initial / benefit change only — regenerating uses the button explicitly.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- avoid refetch on every keystroke
  }, [benefitId, serviceUrl, partnerId])

  useEffect(() => {
    if (!benefitId) {
      setSchemaLoading(false)
      return
    }
    let ignore = false
    ;(async () => {
      setSchemaLoading(true)
      try {
        const { data } = await getFundingTemplateFormSchema(
          serviceUrl,
          partnerId,
          benefitId
        )
        if (ignore) return
        setSchema(data)
        const staticList = data.fields ?? []
        const availableList =
          data.available_fields && data.available_fields.length > 0
            ? data.available_fields
            : staticList
        setStaticFields(staticList)
        setAvailableFields(availableList)
        if (mode === 'create') {
          setAddedPaths([])
          setAddFieldOpen(false)
          const defaults = globalDefaultsRef.current
          const next: FieldOverrideState = {}
          for (const field of staticList) {
            // Seed once per benefit/mode — do not reseed when parent defaults change.
            next[field.path] = {
              mode: 'override',
              value: defaults?.[field.path] ?? '',
            }
          }
          setOverrides(next)
        }
      } catch {
        if (!ignore) {
          setSchema(null)
          setStaticFields([])
          setAvailableFields([])
          setAddedPaths([])
          onError('Failed to load application fields for this funding type.')
        }
      } finally {
        if (!ignore) setSchemaLoading(false)
      }
    })()
    return () => {
      ignore = true
    }
  }, [benefitId, serviceUrl, partnerId, mode, onError])

  // Edit mode: surface non-static override paths as user-added fields.
  useEffect(() => {
    if (mode !== 'edit') return
    if (availableFields.length === 0 && staticFields.length === 0) return
    const staticPaths = new Set(staticFields.map(f => f.path))
    const fromOverrides = Object.keys(template?.field_overrides ?? {}).filter(
      path => !staticPaths.has(path)
    )
    if (fromOverrides.length === 0) return
    setAddedPaths(prev => {
      const available = new Set(availableFields.map(f => f.path))
      const merged = new Set([...prev, ...fromOverrides])
      const next = [...merged].filter(
        path => available.has(path) && !staticPaths.has(path)
      )
      // Hydrating added paths from the server must not look like a user edit
      // (that was flashing “Saving…” on every template open).
      if (
        next.length !== prev.length ||
        next.some((path, i) => path !== prev[i])
      ) {
        if (lastAutosavedRef.current) {
          lastAutosavedRef.current = {
            ...lastAutosavedRef.current,
            addedPaths: next,
          }
        }
        if (pageOpenSnapshotRef.current) {
          pageOpenSnapshotRef.current = {
            ...pageOpenSnapshotRef.current,
            addedPaths: next,
          }
        }
      }
      return next
    })
  }, [mode, template?.field_overrides, staticFields, availableFields])

  const staticPathSet = useMemo(
    () => new Set(staticFields.map(f => f.path)),
    [staticFields]
  )

  const availableByPath = useMemo(() => {
    const map = new Map<string, FundingTemplateFormField>()
    for (const field of availableFields) {
      map.set(field.path, field)
    }
    for (const field of staticFields) {
      if (!map.has(field.path)) map.set(field.path, field)
    }
    return map
  }, [availableFields, staticFields])

  const visibleFields = useMemo(() => {
    const paths: string[] = []
    const seen = new Set<string>()
    for (const field of staticFields) {
      if (seen.has(field.path)) continue
      seen.add(field.path)
      paths.push(field.path)
    }
    for (const path of addedPaths) {
      if (seen.has(path)) continue
      if (!availableByPath.has(path)) continue
      seen.add(path)
      paths.push(path)
    }
    return paths
      .map(path => availableByPath.get(path))
      .filter((f): f is FundingTemplateFormField => Boolean(f))
      // Reusable resources already owns Pricing calculator URL — hide schema dupes.
      .filter(f => !(showPricing && isPricingCalculatorSchemaPath(f.path)))
  }, [staticFields, addedPaths, availableByPath, showPricing])

  const addFieldOptions: SearchableCheckboxOption[] = useMemo(() => {
    const visible = new Set(visibleFields.map(f => f.path))
    return availableFields
      .filter(f => !visible.has(f.path))
      // Same program schema already scopes this list; also hide calculator dupes.
      .filter(f => !(showPricing && isPricingCalculatorSchemaPath(f.path)))
      .map(f => ({
        value: f.path,
        label: schemaFieldDisplayLabel(f),
        description: schemaFieldTooltip(f) ?? undefined,
      }))
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [availableFields, visibleFields, showPricing])

  /**
   * Hide revenue-bound chips that restate the prompt (e.g. "Revenue < $10K"
   * when the AI input already says "less than $10,000"). Do not drop chips
   * whose text equals the description — regenerate fills the input from the
   * leading chip and must still show that chip.
   */
  const visibleSuggestions = useMemo(() => {
    const desc = description.trim().toLowerCase()
    if (!desc) return suggestions
    const mentionsUnder10k =
      /\bless than\b/.test(desc) ||
      /\bunder\b/.test(desc) ||
      desc.includes('<')
    const mentionsOver10k =
      /\bgreater than\b/.test(desc) ||
      /\bmore than\b/.test(desc) ||
      /\bover\b/.test(desc) ||
      desc.includes('>')
    return suggestions.filter(item => {
      const label = item.label.trim().toLowerCase()
      const text = item.text.trim().toLowerCase()
      if (!label && !text) return false
      const chipUnder =
        label.includes('<') ||
        label.includes('less than') ||
        text.includes('less than')
      const chipOver =
        label.includes('>') ||
        label.includes('greater than') ||
        text.includes('greater than')
      const about10k = label.includes('10') || text.includes('10')
      if (mentionsUnder10k && chipUnder && about10k) return false
      if (mentionsOver10k && chipOver && about10k) return false
      return true
    })
  }, [suggestions, description])

  const addField = (path: string) => {
    if (!path || staticPathSet.has(path) || addedPaths.includes(path)) return
    const field = availableByPath.get(path)
    if (!field) return
    setAddedPaths(prev => [...prev, path])
    setAddFieldOpen(false)
    setOverrides(prev => {
      if (prev[path]?.value != null && String(prev[path]?.value).length > 0) {
        return prev
      }
      return {
        ...prev,
        [path]: {
          mode: 'override',
          value: globalDefaults?.[path] ?? '',
        },
      }
    })
  }

  const removeAddedField = (path: string) => {
    if (!addedPaths.includes(path) || staticPathSet.has(path)) return
    setAddedPaths(prev => prev.filter(p => p !== path))
    setOverrides(prev => {
      if (!(path in prev)) return prev
      const next = { ...prev }
      delete next[path]
      return next
    })
    setFieldErrors(prev => {
      if (!(path in prev)) return prev
      const next = { ...prev }
      delete next[path]
      return next
    })
  }

  const removablePathSet = useMemo(() => new Set(addedPaths), [addedPaths])

  const actorBody = useMemo(
    () => ({
      ...(updaterName ? { updated_by: updaterName } : {}),
      ...(updaterLabraUserId
        ? { updated_by_labra_user_id: updaterLabraUserId }
        : {}),
    }),
    [updaterName, updaterLabraUserId]
  )

  const onOverrideChange = (
    path: string,
    next: FundingTemplateFieldOverride
  ) => {
    setOverrides(prev => {
      const merged = { ...prev, [path]: next }
      const errors = validateTemplateFields({
        fields: visibleFields,
        overrides: merged,
        globalDefaults,
        globalsConfigured,
      })
      setFieldErrors(prevErrors => {
        const nextErrors = { ...prevErrors }
        if (errors[path]) nextErrors[path] = errors[path]
        else delete nextErrors[path]
        return nextErrors
      })
      return merged
    })
  }

  const requestToggleActiveInactive = () => {
    if (status === 'archived' || fieldsLocked) return
    setStatusConfirm(status === 'active' ? 'deactivate' : 'activate')
  }

  const confirmToggleActiveInactive = () => {
    if (!statusConfirm) return
    setStatus(statusConfirm === 'deactivate' ? 'disabled' : 'active')
    setStatusConfirm(null)
  }

  const applySnapshotToUi = (snap: PersistedTemplateSnapshot) => {
    setName(snap.name.slice(0, MAX_TEMPLATE_NAME_LENGTH))
    setDescription(snap.description)
    setStatus(snap.status)
    setOverrides({ ...snap.overrides })
    setForceAws(snap.forceAws)
    setCalculatorUrl(snap.calculatorUrl)
    setDocuments([...snap.documents])
    setUploadSlots(slotsFromDocuments(snap.documents))
    setAddedPaths([...snap.addedPaths])
    setAddFieldOpen(false)
    setFieldErrors({})
  }

  const buildCurrentSnapshot = (): PersistedTemplateSnapshot => ({
    name,
    description,
    status,
    overrides,
    forceAws,
    calculatorUrl,
    documents,
    addedPaths,
  })

  /** Restore the page-open (last persisted) version and PATCH it back. */
  const resetToPreviousVersion = async () => {
    if (readOnlyArchived || mode !== 'edit' || !template) return
    const snap = pageOpenSnapshotRef.current
    if (!snap) return
    applySnapshotToUi(snap)
    lastAutosavedRef.current = snap
    setSaving(true)
    try {
      const { data } = await patchFundingTemplate(
        serviceUrl,
        partnerId,
        template.template_id,
        {
          name: snap.name.trim() || null,
          description: snap.description.trim() || null,
          field_overrides: snap.overrides,
          force_aws_account_id: snap.forceAws,
          status: snap.status === 'archived' ? 'disabled' : snap.status,
          resources: {
            documents: snap.documents.map(doc => ({
              s3_uri: doc.s3_uri,
              doc_type: doc.doc_type,
              filename: doc.filename ?? null,
              business_use_case: doc.business_use_case ?? null,
            })),
            ...(snap.calculatorUrl.trim()
              ? { pricing_calculator_url: snap.calculatorUrl.trim() }
              : {}),
          },
          ...actorBody,
        }
      )
      const restored = snapshotFromTemplate(data, snap.addedPaths)
      pageOpenSnapshotRef.current = restored
      lastAutosavedRef.current = restored
      applySnapshotToUi(restored)
      onSaved(data, { kind: 'saved' })
    } catch {
      onError('Failed to reset template to previous version.')
    } finally {
      setSaving(false)
    }
  }

  const resolveTypeOption = (businessUseCase?: string | null) => {
    if (businessUseCase) {
      const match = fileTypes.find(t => t.business_use_case === businessUseCase)
      if (match) return match
    }
    return (
      fileTypes[0] ?? {
        business_use_case: SOW_BUSINESS_USE_CASE,
        doc_type: 'sow',
        label: SOW_BUSINESS_USE_CASE,
      }
    )
  }

  const onUploadDocuments = async (
    fileList: FileList | File[] | null,
    businessUseCase?: string | null,
    slotId?: string | null
  ) => {
    if (!fileList || fileList.length === 0 || !slotId) return
    const files = Array.from(fileList)
    const typeOption = resolveTypeOption(businessUseCase)
    const pending = files.map(file => ({
      id: newPendingUploadId(),
      filename: file.name,
    }))
    setUploadSlots(prev =>
      prev.map(slot =>
        slot.id === slotId
          ? { ...slot, pending: [...slot.pending, ...pending] }
          : slot
      )
    )
    try {
      for (let i = 0; i < files.length; i += 1) {
        const file = files[i]
        const pendingId = pending[i].id
        const docType = toChatDocType(typeOption.doc_type)
        const { data } = await presignChatDocumentAPI(partnerId, {
          filename: file.name,
          doc_type: docType,
          content_type: file.type || 'application/pdf',
        })
        await uploadFileToPresignedUrl(data.upload_url, file, data.content_type)
        const uploaded: FundingTemplateResourceDocument = {
          s3_uri: data.s3_uri,
          doc_type: docType,
          filename: file.name,
          business_use_case: typeOption.business_use_case,
        }
        setUploadSlots(prev => {
          const next = prev.map(slot =>
            slot.id === slotId
              ? {
                  ...slot,
                  documents: [...slot.documents, uploaded],
                  pending: slot.pending.filter(item => item.id !== pendingId),
                }
              : slot
          )
          setDocuments(flattenSlotDocuments(next))
          return next
        })
      }
      setFieldErrors(prev => {
        if (!prev.__documents__) return prev
        const next = { ...prev }
        delete next.__documents__
        return next
      })
    } catch {
      setUploadSlots(prev =>
        prev.map(slot =>
          slot.id === slotId
            ? {
                ...slot,
                pending: slot.pending.filter(
                  item => !pending.some(p => p.id === item.id)
                ),
              }
            : slot
        )
      )
      onError('Failed to upload document.')
    }
  }

  const openFilePickerForSlot = (slotId: string) => {
    activeUploadSlotIdRef.current = slotId
    fileInputRef.current?.click()
  }

  const updateSlotType = (slotId: string, businessUseCase: string) => {
    setUploadSlots(prev => {
      const next = prev.map(slot =>
        slot.id === slotId
          ? {
              ...slot,
              businessUseCase,
              documents: slot.documents.map(doc => ({
                ...doc,
                business_use_case: businessUseCase || doc.business_use_case,
              })),
            }
          : slot
      )
      setDocuments(flattenSlotDocuments(next))
      return next
    })
  }

  const addUploadSlot = () => {
    setUploadSlots(prev => [...prev, emptyUploadSlot()])
  }

  const removeUploadSlot = (slotId: string) => {
    setUploadSlots(prev => {
      const filtered = prev.filter(slot => slot.id !== slotId)
      const next = filtered.length > 0 ? filtered : [emptyUploadSlot()]
      setDocuments(flattenSlotDocuments(next))
      return next
    })
    if (activeUploadSlotIdRef.current === slotId) {
      activeUploadSlotIdRef.current = null
    }
  }

  const removeSlotDocument = (slotId: string, docIndex: number) => {
    setUploadSlots(prev => {
      const next = prev.map(slot =>
        slot.id === slotId
          ? {
              ...slot,
              documents: slot.documents.filter((_, i) => i !== docIndex),
            }
          : slot
      )
      setDocuments(flattenSlotDocuments(next))
      return next
    })
  }

  const buildBody = (): FundingTemplateCreateBody => {
    const sowDoc =
      documents.find(
        d =>
          d.business_use_case === SOW_BUSINESS_USE_CASE ||
          d.doc_type === 'sow' ||
          d.doc_type === 'project_plan'
      ) ?? null
    return {
      benefit_identifier: benefitId,
      name: name.trim() || null,
      description: description.trim() || null,
      field_overrides: overrides,
      force_aws_account_id: forceAws,
      status: status === 'archived' ? 'disabled' : status,
      resources: {
        documents: documents.map(doc => ({
          s3_uri: doc.s3_uri,
          doc_type: doc.doc_type,
          filename: doc.filename ?? null,
          business_use_case: doc.business_use_case ?? null,
        })),
        ...(sowDoc
          ? {
              sow: {
                s3_uri: sowDoc.s3_uri,
                doc_type: sowDoc.doc_type || 'sow',
                filename: sowDoc.filename ?? null,
                business_use_case:
                  sowDoc.business_use_case || SOW_BUSINESS_USE_CASE,
              },
            }
          : {}),
        ...(calculatorUrl.trim()
          ? { pricing_calculator_url: calculatorUrl.trim() }
          : {}),
      },
      ...actorBody,
    }
  }

  const collectFieldErrors = () => {
    const errors = validateTemplateFields({
      fields: visibleFields,
      overrides,
      globalDefaults,
      globalsConfigured,
    })
    const calcErr = validatePricingCalculatorUrl(calculatorUrl)
    if (calcErr) errors.__calculator__ = calcErr
    return errors
  }

  const onSave = async () => {
    const errors = collectFieldErrors()
    setFieldErrors(errors)
    if (Object.keys(errors).length > 0) {
      onError(
        Object.values(errors)[0] || 'Fix validation errors before saving.'
      )
      return
    }

    setSaving(true)
    try {
      let resolvedName = name.trim()
      if (!resolvedName && description.trim() && mode === 'create') {
        try {
          const { data } = await suggestFundingTemplateName(
            serviceUrl,
            partnerId,
            { benefit_identifier: benefitId, description }
          )
          if (data.name) {
            resolvedName = data.name.slice(0, MAX_TEMPLATE_NAME_LENGTH)
            setName(resolvedName)
          }
        } catch {
          // backend may still suggest
        }
      }
      const body = buildBody()
      if (resolvedName) body.name = resolvedName
      let detail: FundingTemplateDetail
      if (mode === 'edit' && template) {
        const { data } = await patchFundingTemplate(
          serviceUrl,
          partnerId,
          template.template_id,
          {
            name: body.name,
            description: body.description,
            field_overrides: body.field_overrides,
            force_aws_account_id: body.force_aws_account_id,
            resources: body.resources,
            status: body.status,
            ...actorBody,
          }
        )
        detail = data
      } else {
        const { data } = await createFundingTemplate(
          serviceUrl,
          partnerId,
          body
        )
        detail = data
      }
      const snap = snapshotFromTemplate(detail, addedPaths)
      pageOpenSnapshotRef.current = snap
      lastAutosavedRef.current = snap
      autosaveGenRef.current += 1
      setAutosaveStatus(mode === 'edit' ? 'saved' : 'idle')
      setFieldErrors({})
      onSaved(detail, { kind: 'saved' })
    } catch (error: unknown) {
      const err = error as {
        response?: {
          status?: number
          data?: { detail?: string; message?: string; errors?: string[] }
        }
        message?: string
      }
      const message =
        err?.response?.data?.errors?.[0] ||
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        err?.message ||
        'Failed to save template.'
      if (err?.response?.status === 409) {
        onError(message || 'A template with this name already exists.')
      } else {
        onError(message)
      }
    } finally {
      setSaving(false)
    }
  }

  // Edit mode: debounced auto-save for all fields after validation passes.
  useEffect(() => {
    if (mode !== 'edit' || !template || readOnlyArchived) return
    if (!pageOpenSnapshotRef.current) return

    const current = buildCurrentSnapshot()
    if (
      lastAutosavedRef.current &&
      snapshotsEqual(current, lastAutosavedRef.current)
    ) {
      // Clear a stuck “Saving…” from a cancelled debounce (common on open while
      // schema / addedPaths hydrate). No network save was in flight yet.
      setAutosaveStatus(prev => (prev === 'saving' ? 'idle' : prev))
      return
    }

    const errors = collectFieldErrors()
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors)
      setAutosaveStatus(prev => (prev === 'saving' ? 'idle' : prev))
      return
    }

    const gen = ++autosaveGenRef.current
    let cancelled = false
    // Do not set “Saving…” until the debounce actually fires a PATCH — otherwise
    // open/hydrate re-renders cancel the timer and leave a permanent Saving label.
    const timer = window.setTimeout(() => {
      if (cancelled) return
      setAutosaveStatus('saving')
      void (async () => {
        try {
          const body = buildBody()
          const { data } = await patchFundingTemplate(
            serviceUrl,
            partnerId,
            template.template_id,
            {
              name: body.name,
              description: body.description,
              field_overrides: body.field_overrides,
              force_aws_account_id: body.force_aws_account_id,
              resources: body.resources,
              status: body.status,
              ...actorBody,
            }
          )
          if (cancelled || gen !== autosaveGenRef.current) return
          const snap = snapshotFromTemplate(data, addedPaths)
          lastAutosavedRef.current = snap
          setAutosaveStatus('saved')
          onSaved(data, { kind: 'autosaved' })
        } catch (error: unknown) {
          if (cancelled || gen !== autosaveGenRef.current) return
          setAutosaveStatus('error')
          const err = error as {
            response?: {
              status?: number
              data?: { detail?: string; message?: string; errors?: string[] }
            }
          }
          const message =
            err?.response?.data?.errors?.[0] ||
            err?.response?.data?.detail ||
            err?.response?.data?.message ||
            'Failed to auto-save template.'
          if (err?.response?.status === 409) {
            onError(message || 'A template with this name already exists.')
          } else {
            onError(message)
          }
        }
      })()
    }, 750)

    return () => {
      cancelled = true
      window.clearTimeout(timer)
    }
    // Intentionally depend on editable field state for debounce.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    mode,
    template?.template_id,
    readOnlyArchived,
    name,
    description,
    status,
    overrides,
    forceAws,
    calculatorUrl,
    documents,
    addedPaths,
  ])

  const onClone = async () => {
    if (!template) return
    setCloning(true)
    try {
      const { data } = await cloneFundingTemplate(
        serviceUrl,
        partnerId,
        template.template_id,
        actorBody
      )
      onCloned(data)
    } catch {
      onError('Failed to clone template.')
    } finally {
      setCloning(false)
    }
  }

  const runArchiveOrUnarchive = async (action: 'archive' | 'unarchive') => {
    if (!template) return
    setSaving(true)
    try {
      if (action === 'unarchive') {
        const { data } = await enableFundingTemplate(
          serviceUrl,
          partnerId,
          template.template_id,
          actorBody
        )
        setStatus(data.status)
        onSaved(data, { kind: 'unarchived' })
      } else {
        const { data } = await archiveFundingTemplate(
          serviceUrl,
          partnerId,
          template.template_id,
          actorBody
        )
        setStatus(data.status)
        onSaved(data, { kind: 'archived' })
      }
    } catch {
      onError(
        action === 'unarchive'
          ? 'Failed to unarchive template.'
          : 'Failed to archive template.'
      )
    } finally {
      setSaving(false)
      setArchiveConfirm(null)
    }
  }

  const requestArchiveOrUnarchive = () => {
    if (!template) return
    setArchiveConfirm(status === 'archived' ? 'unarchive' : 'archive')
  }

  const openActivity = async () => {
    if (!template) return
    setActivityOpen(true)
    setActivityLoading(true)
    try {
      const { data } = await getFundingTemplateActivity(
        serviceUrl,
        partnerId,
        template.template_id
      )
      setActivityEntries(data.entries ?? [])
    } catch {
      setActivityEntries([])
      onError('Failed to load activity log.')
    } finally {
      setActivityLoading(false)
    }
  }

  const openUsages = async () => {
    if (!template) return
    setUsagesOpen(true)
    setUsagesLoading(true)
    try {
      const { data } = await listFundingTemplateUsages(
        serviceUrl,
        partnerId,
        template.template_id
      )
      setUsagesEntries(data.entries ?? [])
    } catch {
      setUsagesEntries([])
      onError('Failed to load template usages.')
    } finally {
      setUsagesLoading(false)
    }
  }

  const archiveConfirmCopy =
    archiveConfirm === 'unarchive' ? UNARCHIVE_CONFIRM : ARCHIVE_CONFIRM

  const statusConfirmCopy =
    statusConfirm === 'activate'
      ? STATUS_ACTIVE_CONFIRM
      : STATUS_INACTIVE_CONFIRM

  const archivedEditTip = readOnlyArchived ? ARCHIVED_EDIT_TOOLTIP : undefined
  const withArchivedEditTip = (node: ReactElement) =>
    archivedEditTip ? (
      <Tooltip title={archivedEditTip} arrow placement="top" enterDelay={250}>
        {node}
      </Tooltip>
    ) : (
      node
    )

  return (
    <div
      ref={pageShellRef}
      className={classes.pageShell}
      data-testid="template-detail-page"
    >
      {readOnlyArchived ? (
        <div
          className={classes.archiveBanner}
          data-testid="template-archived-banner"
          role="status"
        >
          <Typography component="p" className={classes.archiveBannerText}>
            This template is currently archived and cannot be used. You need to
            unarchive it to use it in funding requests.
          </Typography>
          <button
            type="button"
            className={classes.archiveBannerAction}
            onClick={() => setArchiveConfirm('unarchive')}
            disabled={saving}
            data-testid="template-unarchive-banner-action"
          >
            <FontAwesomeIcon icon={faBoxArchiveSolid} />
            Unarchive template
          </button>
        </div>
      ) : null}
      <div
        className={cx(
          classes.contentColumn,
          readOnlyArchived && classes.contentColumnAfterBanner,
        )}
      >
      {/* Breadcrumbs live in the settings header when onHeaderChange is wired. */}
      {!onHeaderChange ? (
        <nav className={classes.breadcrumbs} aria-label="Breadcrumb">
          <button
            type="button"
            className={classes.breadcrumbLink}
            onClick={requestDiscard}
          >
            Funding request
          </button>
          <FontAwesomeIcon
            icon={faChevronRight}
            className={cx(classes.breadcrumbSep, classes.breadcrumbSepCurrent)}
            aria-hidden
          />
          <span
            className={classes.breadcrumbCurrent}
            data-testid="template-breadcrumb-name"
          >
            {breadcrumbName}
          </span>
        </nav>
      ) : null}

      <section
        className={cx(classes.card, classes.summaryCard)}
        data-testid="template-status-card"
      >
        {mode === 'edit' && !template ? (
          <div
            role="status"
            aria-label="Loading template details"
            data-testid="template-summary-skeleton"
          >
            <div className={classes.summaryGrid}>
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className={classes.summaryField}>
                  <span
                    className={cx(classes.skeletonBar, classes.skeletonLabel)}
                  />
                  <span
                    className={cx(classes.skeletonBar, classes.skeletonValue)}
                  />
                </div>
              ))}
            </div>
            <div className={classes.skeletonMetaStack}>
              <span
                className={cx(classes.skeletonBar, classes.skeletonBlock)}
              />
              <span
                className={cx(classes.skeletonBar, classes.skeletonBlock)}
              />
            </div>
          </div>
        ) : (
        <div className={classes.summaryFields}>
        <div className={classes.summaryGrid}>
          <div className={classes.summaryField}>
            <Typography component="p" className={classes.summaryLabel}>
              Template name
            </Typography>
            {withArchivedEditTip(
              <div className={classes.nameRow}>
                <input
                  ref={nameInputRef}
                  className={classes.nameInput}
                  value={name}
                  aria-label="Template name"
                  disabled={fieldsLocked}
                  placeholder={DEFAULT_NEW_TEMPLATE_NAME}
                  maxLength={MAX_TEMPLATE_NAME_LENGTH}
                  onChange={e =>
                    setName(e.target.value.slice(0, MAX_TEMPLATE_NAME_LENGTH))
                  }
                />
                {!fieldsLocked ? (
                  <button
                    type="button"
                    className={classes.nameEditButton}
                    aria-label="Edit template name"
                    data-testid="template-name-edit"
                    onClick={() => {
                      nameInputRef.current?.focus()
                      nameInputRef.current?.select()
                    }}
                  >
                    <FontAwesomeIcon icon={faPen} />
                  </button>
                ) : null}
              </div>
            )}
          </div>
          <div className={classes.summaryField}>
            <Typography component="p" className={classes.summaryLabel}>
              Funding program name
            </Typography>
            <Typography component="p" className={classes.summaryValue}>
              {subFundingLabel}
            </Typography>
          </div>
          <div className={classes.summaryField}>
            <Typography component="p" className={classes.summaryLabel}>
              Used in
            </Typography>
            <Typography component="p" className={classes.summaryValue}>
              {mode === 'edit' && template ? (
                <button
                  type="button"
                  className={classes.usedInLink}
                  onClick={() => void openUsages()}
                  data-testid="template-used-in-link"
                >
                  {(() => {
                    const n = Number(template.funding_request_count ?? 0)
                    if (n <= 0) return 'funding requests'
                    return `${n} funding request${n === 1 ? '' : 's'}`
                  })()}
                </button>
              ) : (
                <span className={classes.summaryValueMuted}>—</span>
              )}
            </Typography>
          </div>
          <div className={classes.summaryField}>
            <Typography component="p" className={classes.summaryLabel}>
              Created by
            </Typography>
            <Typography component="p" className={classes.summaryValue}>
              {mode === 'edit' && template
                ? createdByLine(template.created_by, template.created_at)
                : '—'}
            </Typography>
          </div>
          <div className={classes.summaryField}>
            <Typography component="p" className={classes.summaryLabel}>
              Last updated
            </Typography>
            <Typography
              component="p"
              className={cx(
                classes.summaryValue,
                !(mode === 'edit' && template?.updated_at) &&
                  classes.summaryValueMuted
              )}
            >
              {mode === 'edit' && template
                ? (() => {
                    const when = formatWalletDate(template.updated_at)
                    const who = template.updated_by?.trim()
                    if (who && when) return `${when} by ${who}`
                    if (when) return when
                    if (who) return `by ${who}`
                    return '—'
                  })()
                : '—'}
            </Typography>
          </div>
          <div className={classes.summaryField}>
            <Typography component="p" className={classes.summaryLabel}>
              Status
            </Typography>
            {status === 'archived' ? (
              <span
                className={cx(classes.statusPill, classes.statusPillMuted)}
                data-testid="template-status-banner"
              >
                ARCHIVED
              </span>
            ) : (
              <button
                type="button"
                role="switch"
                aria-checked={status === 'active'}
                aria-label="Toggle active status"
                data-testid="template-status-banner"
                className={cx(
                  classes.statusPillToggle,
                  status === 'active' && classes.statusPillToggleOn
                )}
                disabled={fieldsLocked}
                onClick={requestToggleActiveInactive}
              >
                <span
                  className={cx(
                    classes.statusPillToggleLabel,
                    status === 'active' && classes.statusPillToggleLabelOn
                  )}
                >
                  {status === 'active' ? 'ACTIVE' : 'INACTIVE'}
                </span>
                <span
                  className={cx(
                    classes.statusPillToggleKnob,
                    status === 'active' && classes.statusPillToggleKnobOn
                  )}
                  aria-hidden
                />
              </button>
            )}
          </div>
        </div>

        <div className={classes.metaReadonlyBlock}>
          <Typography component="p" className={classes.summaryLabel}>
            Eligibility criteria
          </Typography>
          <Typography
            component="p"
            className={classes.metaReadonlyText}
            data-testid="template-eligibility"
          >
            {eligibilityCriteria?.trim() || 'Not provided for this program.'}
          </Typography>
        </div>

        <div className={classes.metaReadonlyBlock}>
          <Typography component="p" className={classes.summaryLabel}>
            Program description
          </Typography>
          <Typography
            component="p"
            className={classes.metaReadonlyText}
            data-testid="template-program-description"
          >
            {programDescription?.trim() || 'Not provided for this program.'}
          </Typography>
        </div>
        </div>
        )}

        {!templateDetailPending && mode === 'edit' ? (
          <>
            <hr className={classes.divider} />
            <div className={classes.summaryActions}>
              <button
                type="button"
                className={classes.outlineButton}
                onClick={() => void onClone()}
                disabled={cloning || saving}
              >
                <span className={classes.outlineButtonIcon} aria-hidden>
                  <FontAwesomeIcon icon={faClone} />
                </span>
                <span className={classes.outlineButtonLabel}>Clone</span>
              </button>
              <button
                type="button"
                className={classes.outlineButton}
                onClick={() => void openActivity()}
              >
                <span className={classes.outlineButtonIcon} aria-hidden>
                  <FontAwesomeIcon icon={faClockRotateLeft} />
                </span>
                <span className={classes.outlineButtonLabel}>
                  View activity log
                </span>
              </button>
              <button
                type="button"
                className={classes.outlineButton}
                onClick={requestArchiveOrUnarchive}
                disabled={saving}
                data-testid="template-archive-action"
              >
                <span className={classes.outlineButtonIcon} aria-hidden>
                  <FontAwesomeIcon
                    icon={status === 'archived' ? faBoxArchive : faArchive}
                  />
                </span>
                <span className={classes.outlineButtonLabel}>
                  {status === 'archived' ? 'Unarchive' : 'Archive'}
                </span>
              </button>
            </div>
          </>
        ) : null}
      </section>

      <section className={classes.card} data-testid="template-ai-input-section">
        <div className={classes.cardTitleGroup}>
          <span className={classes.iconBox}>
            <FontAwesomeIcon icon={faSparkles} />
          </span>
          <Typography component="h2" className={classes.cardTitle}>
            When will this template be used?
          </Typography>
        </div>
        <div className={classes.aiSectionBody}>
          <Typography
            component="p"
            className={cx(
              classes.aiPromptLabel,
              readOnlyArchived && classes.aiPromptLabelMuted
            )}
          >
            Describe in plain language when this template should be picked.
          </Typography>
          <div
            className={cx(
              classes.aiCard,
              readOnlyArchived && classes.aiCardArchived
            )}
          >
            {isRegenerating ? (
              <div
                className={cx(classes.skeletonBar, classes.skeletonTextarea)}
                role="status"
                aria-label="Re-writing AI input"
                data-testid="template-ai-input-skeleton"
              />
            ) : (
              withArchivedEditTip(
                <span className={classes.archivedFieldTipTarget}>
                  <textarea
                    className={classes.aiTextarea}
                    value={description}
                    aria-label="AI input"
                    disabled={fieldsLocked || templateDetailPending}
                    rows={4}
                    placeholder="Describe the opportunities this profile should be used for in natural-language. Leave it blank to make the profile manual-selection only…"
                    onChange={e => setDescription(e.target.value)}
                  />
                </span>
              )
            )}
            {!readOnlyArchived ? (
              <div className={classes.aiSuggestions}>
                <p className={classes.aiSuggestionsLabel}>
                  <FontAwesomeIcon
                    icon={faSparkles}
                    className={classes.aiSparkle}
                  />
                  AI suggested conditions
                </p>
                <div className={classes.aiSuggestionsRow}>
                  {suggestionsLoading || isRegenerating ? (
                    <div
                      className={classes.skeletonChipRow}
                      role="status"
                      aria-label="Loading suggestions"
                      data-testid="template-ai-chips-skeleton"
                    >
                      <span
                        className={cx(classes.skeletonBar, classes.skeletonChip)}
                      />
                      <span
                        className={cx(classes.skeletonBar, classes.skeletonChip)}
                      />
                      <span
                        className={cx(classes.skeletonBar, classes.skeletonChip)}
                      />
                    </div>
                  ) : (
                    <div className={classes.chips}>
                      {visibleSuggestions.map(item => (
                        <button
                          key={item.label}
                          type="button"
                          className={classes.chip}
                          title={item.label}
                          onClick={() => setDescription(item.text)}
                        >
                          {item.label}
                        </button>
                      ))}
                    </div>
                  )}
                  <button
                    type="button"
                    className={classes.regenerateButton}
                    data-testid="regenerate-ai-suggestions"
                    disabled={isRegenerating || !benefitId}
                    onClick={() => void loadSuggestions({ regenerate: true })}
                  >
                    <FontAwesomeIcon icon={faArrowRotateLeft} />
                    {isRegenerating ? 'Re-writing…' : 'Re-write'}
                  </button>
                </div>
              </div>
            ) : null}
          </div>
          <div
            className={classes.autoSubmitBanner}
            data-testid="template-autosubmit-banner"
          >
            <AutoSubmitLimitNote limitUsd={autoSubmitLimitUsd} />
          </div>
        </div>
      </section>

      <section className={classes.card}>
        <div className={classes.cardTitleGroup}>
          <span className={classes.iconBox}>
            <FontAwesomeIcon icon={faListUl} />
          </span>
          <Typography component="h2" className={classes.cardTitle}>
            Basic information
          </Typography>
        </div>
        {schemaLoading ? (
          <div
            className={classes.skeletonFieldGrid}
            role="status"
            aria-label="Loading fields"
            data-testid="template-fields-skeleton"
          >
            {Array.from({ length: 6 }).map((_, i) => (
              <span
                key={i}
                className={cx(classes.skeletonBar, classes.skeletonField)}
              />
            ))}
          </div>
        ) : (
          <>
            <DynamicSchemaFields
              layout="grid"
              groupFields={false}
              fields={visibleFields}
              overrides={overrides}
              globalDefaults={globalDefaults}
              globalsConfigured={globalsConfigured}
              onChange={onOverrideChange}
              disabled={fieldsLocked || templateDetailPending}
              disabledTooltip={archivedEditTip}
              fieldErrors={fieldErrors}
              removablePaths={removablePathSet}
              onRemoveField={removeAddedField}
            />
            {!readOnlyArchived &&
            !templateDetailPending &&
            addFieldOptions.length > 0 ? (
              <div
                className={classes.addFieldRow}
                data-testid="template-add-field"
              >
                {addFieldOpen ? (
                  <SearchableCheckboxSelect
                    id="template-add-schema-field"
                    label="Select a field to add"
                    options={addFieldOptions}
                    value={[]}
                    maxSelections={1}
                    placeholder="Search fields…"
                    onChange={next => {
                      const path = next[0]
                      if (path) addField(path)
                    }}
                  />
                ) : (
                  <button
                    type="button"
                    className={classes.addFieldButton}
                    data-testid="template-add-field-button"
                    onClick={() => setAddFieldOpen(true)}
                  >
                    <FontAwesomeIcon icon={faPlus} />
                    Add field
                  </button>
                )}
              </div>
            ) : null}
          </>
        )}
      </section>

      {showResourcesCard ? (
        <section className={classes.card} data-testid="template-resources">
          <div className={classes.cardTitleGroup}>
            <span className={classes.iconBox}>
              <FontAwesomeIcon icon={faFileLines} />
            </span>
            <div className={classes.resourcesTitleRow}>
              <Typography component="h2" className={classes.cardTitle}>
                Reusable resources
              </Typography>
              <Typography component="span" className={classes.optionalBadge}>
                Optional
              </Typography>
            </div>
          </div>

          {showPricing ? (
            <div className={classes.fieldBlock}>
              {withArchivedEditTip(
                <div
                  className={cx(
                    classes.floatField,
                    (calculatorFocused || Boolean(calculatorUrl.trim())) &&
                      classes.floatFieldFloated,
                    fieldErrors.__calculator__ && classes.floatFieldError
                  )}
                >
                  <Typography
                    component="span"
                    className={cx(
                      classes.floatLabelInField,
                      (calculatorFocused || Boolean(calculatorUrl.trim())) &&
                        classes.floatLabelFloated
                    )}
                  >
                    Pricing calculator URL
                  </Typography>
                  <input
                    className={classes.floatInput}
                    value={calculatorUrl}
                    aria-label="Pricing calculator URL"
                    disabled={fieldsLocked}
                    onFocus={() => setCalculatorFocused(true)}
                    onBlur={() => {
                      setCalculatorFocused(false)
                      const err = validatePricingCalculatorUrl(calculatorUrl)
                      setFieldErrors(prev => {
                        const next = { ...prev }
                        if (err) next.__calculator__ = err
                        else delete next.__calculator__
                        return next
                      })
                    }}
                    onChange={event => {
                      setCalculatorUrl(event.target.value)
                      setFieldErrors(prev => {
                        if (!prev.__calculator__) return prev
                        const next = { ...prev }
                        delete next.__calculator__
                        return next
                      })
                    }}
                  />
                </div>
              )}
              {fieldErrors.__calculator__ ? (
                <Typography component="p" className={classes.sowError}>
                  {fieldErrors.__calculator__}
                </Typography>
              ) : null}
            </div>
          ) : null}

          {showTypedAttach ? (
            <div className={classes.fieldBlock}>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.doc,.docx"
                multiple
                aria-label="Upload document"
                data-testid="template-document-file-input"
                className={classes.hiddenFileInput}
                disabled={fieldsLocked}
                onChange={event => {
                  const slotId = activeUploadSlotIdRef.current
                  const slot = uploadSlots.find(s => s.id === slotId)
                  const businessUseCase = slot?.businessUseCase
                  if (!businessUseCase || !slotId) {
                    onError('Select a document type before uploading.')
                    activeUploadSlotIdRef.current = null
                    event.target.value = ''
                    return
                  }
                  void onUploadDocuments(
                    event.target.files,
                    businessUseCase,
                    slotId
                  )
                  activeUploadSlotIdRef.current = null
                  event.target.value = ''
                }}
              />
              <div
                className={classes.uploadPanel}
                data-testid="template-document-upload-zone"
              >
                {uploadSlots.length > 0 ? (
                  <div
                    className={classes.uploadAddRows}
                    data-testid="template-document-list"
                  >
                    {uploadSlots.map((slot, slotIndex) => {
                      const isUploading = slot.pending.length > 0
                      const slotLocked = fieldsLocked || isUploading
                      const uploadRow = (
                        <div
                          className={classes.uploadAddRow}
                          data-testid={
                            slotIndex === 0
                              ? 'template-document-add-row'
                              : `template-document-add-row-${slotIndex}`
                          }
                        >
                          <div className={classes.uploadPanelType}>
                            <SearchableCheckboxSelect
                              id={`upload-slot-type-${slot.id}`}
                              label="Document type"
                              options={fileTypeOptions}
                              value={
                                slot.businessUseCase
                                  ? [slot.businessUseCase]
                                  : []
                              }
                              maxSelections={1}
                              floatingLabel
                              disabled={slotLocked}
                              placeholder=""
                              onChange={next => {
                                const nextType = next[next.length - 1] ?? ''
                                updateSlotType(slot.id, nextType)
                              }}
                            />
                          </div>
                          <div className={classes.uploadActions}>
                            <button
                              type="button"
                              className={classes.uploadButton}
                              disabled={
                                fieldsLocked ||
                                !slot.businessUseCase ||
                                isUploading
                              }
                              aria-busy={isUploading}
                              onClick={() => {
                                if (fieldsLocked) return
                                if (!slot.businessUseCase) {
                                  onError(
                                    'Select a document type before uploading.'
                                  )
                                  return
                                }
                                openFilePickerForSlot(slot.id)
                              }}
                            >
                              {isUploading ? (
                                <CircularProgress
                                  size={16}
                                  thickness={5}
                                  className={classes.uploadSpinner}
                                />
                              ) : null}
                              {isUploading ? 'Uploading…' : 'Upload files'}
                            </button>
                            <Tooltip
                              title={
                                schema?.resources.file_description?.trim() ||
                                DEFAULT_FILE_HELP
                              }
                              arrow
                              placement="top"
                            >
                              <span className={classes.uploadInfoWrap}>
                                <FontAwesomeIcon
                                  icon={faCircleInfo}
                                  className={classes.dropzoneInfo}
                                />
                              </span>
                            </Tooltip>
                            <div className={classes.uploadInlineFiles}>
                              {slot.pending.map(item => (
                                <span
                                  key={item.id}
                                  className={classes.uploadInlineFile}
                                  data-testid={`template-document-pending-${item.id}`}
                                >
                                  <CircularProgress
                                    size={14}
                                    thickness={5}
                                    className={classes.uploadSpinner}
                                  />
                                  <Typography
                                    component="span"
                                    className={classes.documentName}
                                  >
                                    {item.filename}
                                  </Typography>
                                </span>
                              ))}
                              {slot.documents.map((doc, docIndex) => (
                                <span
                                  key={`${doc.s3_uri}-${docIndex}`}
                                  className={classes.uploadInlineFile}
                                  data-testid={
                                    slotIndex === 0 && docIndex === 0
                                      ? 'template-document-row-0'
                                      : `template-document-row-${slotIndex}-${docIndex}`
                                  }
                                >
                                  <Typography
                                    component="span"
                                    className={classes.documentName}
                                  >
                                    {doc.filename || doc.s3_uri}
                                  </Typography>
                                  {!fieldsLocked ? (
                                    <button
                                      type="button"
                                      className={classes.removeDocButton}
                                      aria-label={`Remove ${doc.filename || 'document'}`}
                                      onClick={() =>
                                        removeSlotDocument(slot.id, docIndex)
                                      }
                                    >
                                      <FontAwesomeIcon icon={faXmark} />
                                    </button>
                                  ) : null}
                                </span>
                              ))}
                            </div>
                            {!fieldsLocked ? (
                              <button
                                type="button"
                                className={classes.removeDocButton}
                                aria-label="Remove upload row"
                                data-testid={`template-document-remove-row-${slotIndex}`}
                                disabled={isUploading}
                                onClick={() => removeUploadSlot(slot.id)}
                              >
                                <FontAwesomeIcon icon={faTrashCan} />
                              </button>
                            ) : null}
                          </div>
                        </div>
                      )
                      return (
                        <Fragment key={slot.id}>
                          {withArchivedEditTip(uploadRow)}
                        </Fragment>
                      )
                    })}
                    {!fieldsLocked ? (
                      <button
                        type="button"
                        className={classes.addUploadRowButton}
                        onClick={addUploadSlot}
                        data-testid="template-document-add-another"
                      >
                        <FontAwesomeIcon icon={faPlus} />
                        Add another file
                      </button>
                    ) : null}
                  </div>
                ) : null}
              </div>
              {fieldErrors.__documents__ ? (
                <Typography component="p" className={classes.sowError}>
                  {fieldErrors.__documents__}
                </Typography>
              ) : null}
            </div>
          ) : null}
        </section>
      ) : null}
      </div>

      <div className={classes.bottomBar} data-testid="template-detail-bottom-bar">
        <div className={classes.bottomBarInner}>
          <div className={classes.bottomBarActions}>
            {mode === 'edit' && !readOnlyArchived
              ? (() => {
                  const hint =
                    Object.keys(fieldErrors).length > 0
                      ? 'Fix validation errors to save.'
                      : autosaveStatus === 'saving'
                        ? 'Saving…'
                        : autosaveStatus === 'saved'
                          ? 'Saved'
                          : autosaveStatus === 'error'
                            ? 'Save failed'
                            : null
                  return hint ? (
                    <Typography component="span" className={classes.autosaveHint}>
                      {hint}
                    </Typography>
                  ) : null
                })()
              : null}
            {!readOnlyArchived && mode === 'edit' ? (
              <button
                type="button"
                className={classes.resetDefaultButton}
                onClick={() => void resetToPreviousVersion()}
                disabled={
                  saving ||
                  cloning ||
                  autosaveStatus === 'saving' ||
                  isRegenerating
                }
                data-testid="template-reset-previous"
              >
                Reset
              </button>
            ) : null}
            <button
              type="button"
              className={classes.secondaryButton}
              onClick={requestDiscard}
              disabled={saving || cloning || isRegenerating}
            >
              Discard
            </button>
            {!readOnlyArchived ? (
              <button
                type="button"
                className={classes.primaryButton}
                onClick={() => void onSave()}
                disabled={
                  saving ||
                  cloning ||
                  isRegenerating ||
                  !benefitId ||
                  autosaveStatus === 'saving'
                }
                data-testid="template-save"
              >
                {saving || autosaveStatus === 'saving' ? 'Saving…' : 'Save'}
              </button>
            ) : null}
          </div>
        </div>
      </div>

      <TemplateActivityLogModal
        open={activityOpen}
        onClose={() => setActivityOpen(false)}
        entries={activityEntries}
        loading={activityLoading}
      />

      <TemplateUsagesModal
        open={usagesOpen}
        onClose={() => setUsagesOpen(false)}
        entries={usagesEntries}
        loading={usagesLoading}
        onOpenRequest={entry => {
          const program = (entry.program || '').trim()
          if (!program || !entry.workflow_id) return
          navigate(
            `../requests/${encodeURIComponent(program)}/${encodeURIComponent(
              entry.workflow_id
            )}`
          )
        }}
      />

      <FundingConfirmDialog
        open={archiveConfirm != null}
        title={archiveConfirmCopy.title}
        body={archiveConfirmCopy.body}
        confirmLabel={archiveConfirmCopy.confirmLabel}
        confirmTestId="template-archive-confirm"
        onCancel={() => setArchiveConfirm(null)}
        onConfirm={() => {
          if (archiveConfirm) void runArchiveOrUnarchive(archiveConfirm)
        }}
      />

      <FundingConfirmDialog
        open={statusConfirm != null}
        title={statusConfirmCopy.title}
        body={statusConfirmCopy.body}
        confirmLabel={statusConfirmCopy.confirmLabel}
        icon={statusConfirm === 'activate' ? faToggleOn : faToggleOff}
        confirmTestId="template-status-confirm"
        onCancel={() => setStatusConfirm(null)}
        onConfirm={confirmToggleActiveInactive}
      />

      <FundingConfirmDialog
        open={createUnsavedOpen}
        title="Unsaved changes"
        body="You have unsaved changes. Please click Save."
        confirmLabel="Discard anyway"
        confirmTestId="template-create-unsaved-discard"
        onCancel={() => setCreateUnsavedOpen(false)}
        onConfirm={() => {
          setCreateUnsavedOpen(false)
          onDiscard()
        }}
      />
    </div>
  )
}

export default TemplateDetailPage
